import { useEffect, useState } from "react";
import { cents, entities, useObjectMemo } from "byzantine";
import Transaction from "byzantine/src/Transaction";
import { useLoadingContext } from "cerulean/src";
import filters from "byzantine/src/filters";
import type Account from "byzantine/src/Account";

import TransactionDetailsDialog from "../TransactionDetails";
import NewThreadDialog from "../NewThreadDialog";

const { useGetTransaction } = entities.transactions;

interface UseViewTransactionContainer {
  loading: boolean;
  isDialogOpen: boolean;
  hideDialog: () => void;
  isReportIssueOpen: boolean;
  toggleIsReportIssueOpen: () => void;
  transaction: Transaction | undefined;
}

interface ViewTransactionProps {
  account?: Account;
  transactionId?: string;
}

const useViewTransactionContainer = ({
  transactionId = "",
  account,
}: ViewTransactionProps): UseViewTransactionContainer => {
  const { setIsLoading: setIsLoadingContext } = useLoadingContext();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState<boolean>(false);

  const showDialog = () => setIsDialogOpen(true);
  const hideDialog = () => setIsDialogOpen(false);
  const toggleIsReportIssueOpen = () => setIsReportIssueOpen((prev) => !prev);

  const { data, loading } = useGetTransaction(transactionId, {
    onSuccess: showDialog,
  });

  useEffect(() => {
    setIsLoadingContext(loading);
  }, [loading]);

  return useObjectMemo({
    loading,
    isDialogOpen,
    hideDialog,
    isReportIssueOpen,
    toggleIsReportIssueOpen,
    transaction: data?.transaction
      ? Transaction.deserialize(data?.transaction, account)
      : undefined,
  });
};

const ViewTransaction = ({ transactionId, account }: ViewTransactionProps) => {
  const {
    isDialogOpen,
    hideDialog,
    transaction,
    isReportIssueOpen,
    toggleIsReportIssueOpen,
  } = useViewTransactionContainer({ transactionId, account });

  if (!transactionId || !transaction || !account) {
    return null;
  }

  const {
    description,
    amount,
    resulting_balance: resultingBalance,
  } = transaction;
  const displayedAmount = filters.currency(cents(amount), {
    hasSign: true,
  });
  const displayedResultingBalance = filters.currency(cents(resultingBalance));

  return (
    <>
      <TransactionDetailsDialog
        transaction={transaction}
        transactionTitle={description}
        displayedAmount={displayedAmount}
        displayedResultingBalance={displayedResultingBalance}
        isSourceMultipleAccounts={false}
        isDialogOpen={isDialogOpen}
        closeDialog={hideDialog}
        ReportIssueCallback={toggleIsReportIssueOpen}
      />
      <NewThreadDialog
        accountNumber={account.number}
        transactionId={transaction.id}
        isDialogOpen={isReportIssueOpen}
        closeDialog={toggleIsReportIssueOpen}
        replyTime={""}
      />
    </>
  );
};

export default ViewTransaction;
